<template>
    <PtDialog
        :visible="dialogVisible"
        :title="t('patentEditor.userPreferences.title')"
        class="user-preferences-dialog-modal"
        @close="handleClose"
    >
        <template #title>
            <div class="user-preferences-dialog__title-layout">
                <button
                    type="button"
                    class="user-preferences-dialog__title-nav"
                    aria-current="page"
                >
                    <WandSparkles class="user-preferences-dialog__nav-icon" :size="14" :stroke-width="1.75" />
                    <span>{{ t('patentEditor.userPreferences.personalization') }}</span>
                </button>
                <div class="user-preferences-dialog__task-tabs">
                    <button
                        v-for="option in taskOptions"
                        :key="option.value"
                        class="user-preferences-dialog__task-tab"
                        :class="{ 'is-active': activeTaskType === option.value }"
                        :disabled="saving"
                        @click="handleTaskTypeChange(option.value)"
                    >
                        {{ t(option.labelKey) }}
                    </button>
                </div>
            </div>
        </template>

        <div class="user-preferences-dialog">
            <aside class="user-preferences-dialog__nav" aria-hidden="true" />

            <section class="user-preferences-dialog__content">
                <p class="user-preferences-dialog__desc">
                    {{ t('patentEditor.userPreferences.description', { taskType: activeTaskTypeName }) }}
                </p>

                <textarea
                    v-model="content"
                    class="user-preferences-dialog__textarea"
                    :disabled="loading || saving"
                    :placeholder="t('patentEditor.userPreferences.placeholder')"
                />
            </section>
        </div>

        <template #footer>
            <div class="flex justify-end gap-3">
                <button
                    class="th-btn th-btn-secondary"
                    :disabled="saving"
                    @click="handleClose"
                >
                    {{ t('global.btn.cancel') }}
                </button>
                <button
                    class="th-btn"
                    :disabled="loading || saving"
                    @click="handleSave"
                >
                    {{ saving ? t('patentEditor.userPreferences.saving') : t('patentEditor.draftingHeader.save') }}
                </button>
            </div>
        </template>
    </PtDialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { ElMessageBox } from 'element-plus';
import PtDialog from '@patsnap-biz/dialog';
import { Toast } from 'patsnap-biz';
import { useI18n } from 'vue-i18n';
import { WandSparkles } from 'lucide-vue-next';
import { getAgentExecutionPreferences, saveAgentExecutionPreferences } from '@/features/ai-settings/api';
import type { PreferenceTaskType } from '@/features/ai-settings/types';
import { AgentType } from '@/features/drafting/constants/enum';

interface Props {
    visible: boolean;
    initialTaskType?: PreferenceTaskType;
}

const props = withDefaults(defineProps<Props>(), {
    initialTaskType: AgentType.PATENT_DRAFT,
});
const emit = defineEmits<{
    (e: 'update:visible', value: boolean): void;
}>();

const { t } = useI18n();
const loading = ref(false);
const saving = ref(false);
const content = ref('');
const originalContent = ref('');
const activeTaskType = ref<PreferenceTaskType>(props.initialTaskType);
let loadRequestId = 0;

const taskOptions: Array<{ value: PreferenceTaskType; labelKey: string }> = [
    { value: AgentType.DISCLOSURE, labelKey: 'patentEditor.userPreferences.taskDisclosure' },
    { value: AgentType.PATENT_DRAFT, labelKey: 'patentEditor.userPreferences.taskPatentDraft' },
    { value: AgentType.PROSECUTION, labelKey: 'patentEditor.userPreferences.taskProsecution' },
];

const dialogVisible = computed({
    get: () => props.visible,
    set: (value: boolean) => emit('update:visible', value),
});

const isDirty = computed(() => content.value !== originalContent.value);
const activeTaskTypeName = computed(() => {
    const option = taskOptions.find(item => item.value === activeTaskType.value);
    return option ? t(option.labelKey) : '';
});

watch(
    () => props.visible,
    (visible) => {
        if (visible) {
            activeTaskType.value = props.initialTaskType;
            content.value = '';
            originalContent.value = '';
            void loadPreferences(activeTaskType.value);
        } else {
            loadRequestId += 1;
            loading.value = false;
        }
    },
    { immediate: true },
);

async function loadPreferences(taskType: PreferenceTaskType): Promise<'loaded' | 'failed' | 'stale'> {
    const requestId = ++loadRequestId;
    loading.value = true;
    try {
        const setting = await getAgentExecutionPreferences(taskType);
        if (requestId !== loadRequestId || activeTaskType.value !== taskType) return 'stale';
        content.value = setting.content || '';
        originalContent.value = content.value;
        return 'loaded';
    } catch (error) {
        if (requestId !== loadRequestId || activeTaskType.value !== taskType) return 'stale';
        console.error('Load user execution preferences failed:', error);
        Toast({ type: 'error', message: t('patentEditor.userPreferences.loadFailed') });
        return 'failed';
    } finally {
        if (requestId === loadRequestId) loading.value = false;
    }
}

async function confirmDiscardChanges(messageKey: string): Promise<boolean> {
    if (!isDirty.value) return true;
    try {
        await ElMessageBox.confirm(
            t(messageKey),
            t('patentEditor.userPreferences.closeConfirmTitle'),
            {
                type: 'warning',
                confirmButtonText: t('patentEditor.userPreferences.closeConfirmOk'),
                cancelButtonText: t('global.btn.cancel'),
            },
        );
        return true;
    } catch {
        return false;
    }
}

async function handleTaskTypeChange(taskType: PreferenceTaskType) {
    if (taskType === activeTaskType.value || saving.value) return;
    if (!await confirmDiscardChanges('patentEditor.userPreferences.switchConfirmMessage')) return;

    const previousTaskType = activeTaskType.value;
    const previousContent = content.value;
    const previousOriginalContent = originalContent.value;
    const previousWasLoading = loading.value;
    activeTaskType.value = taskType;
    content.value = '';
    originalContent.value = '';

    const result = await loadPreferences(taskType);
    if (result === 'failed' && activeTaskType.value === taskType) {
        activeTaskType.value = previousTaskType;
        content.value = previousContent;
        originalContent.value = previousOriginalContent;
        if (previousWasLoading) void loadPreferences(previousTaskType);
    }
}

async function handleClose() {
    if (!await confirmDiscardChanges('patentEditor.userPreferences.closeConfirmMessage')) return;
    dialogVisible.value = false;
}

async function handleSave() {
    saving.value = true;
    try {
        const setting = await saveAgentExecutionPreferences(activeTaskType.value, content.value);
        originalContent.value = setting.content || '';
        content.value = originalContent.value;
        Toast({ type: 'success', message: t('patentEditor.userPreferences.saveSuccess') });
        dialogVisible.value = false;
    } catch (error) {
        console.error('Save user execution preferences failed:', error);
        Toast({ type: 'error', message: t('patentEditor.userPreferences.saveFailed') });
    } finally {
        saving.value = false;
    }
}
</script>

<style lang="scss">
.user-preferences-dialog-modal .th-modal-box {
    width: 720px;
    max-width: calc(100vw - 32px) !important;
    background: linear-gradient(to right, #f5f6f8 0, #f5f6f8 176px, #fff 176px, #fff 100%);
}

.user-preferences-dialog__title-layout {
    display: flex;
    flex: 1;
    align-items: center;
    min-width: 0;
}

.user-preferences-dialog__title-nav {
    display: flex;
    flex: 0 0 152px;
    align-items: center;
    gap: 6px;
    margin: 0 8px 0 -8px;
    padding: 8px;
    box-sizing: border-box;
    font-size: 14px;
    font-weight: 600;
    color: #1d2129;
    text-align: left;
    background-color: #e5e7eb;
    border: none;
    border-radius: 6px;
    cursor: pointer;

    &:hover {
        background-color: #dfe1e6;
    }
}

.user-preferences-dialog {
    display: flex;
    min-height: 420px;
}

.user-preferences-dialog__nav {
    width: 152px;
    box-sizing: border-box;
    border-right: 1px solid #e5e7eb;
}

.user-preferences-dialog__nav-icon {
    flex-shrink: 0;
}

.user-preferences-dialog__content {
    flex: 1;
    min-width: 0;
    padding-left: 16px;
}

.user-preferences-dialog__task-tabs {
    display: grid;
    flex: 1;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0;
    min-width: 0;
    margin-left: 16px;
}

.user-preferences-dialog__task-tab {
    position: relative;
    min-width: 0;
    padding: 8px 12px;
    overflow: hidden;
    font-size: 13px;
    font-weight: 500;
    color: #4e5969;
    text-overflow: ellipsis;
    white-space: nowrap;
    background-color: transparent;
    border: none;
    border-radius: 6px;
    cursor: pointer;

    &:hover:not(:disabled) {
        background-color: #f2f3f5;
    }

    & + &::before {
        position: absolute;
        top: 50%;
        left: 0;
        width: 1px;
        height: 20px;
        content: '';
        background-color: #c9cdd4;
        transform: translateY(-50%);
    }

    &.is-active {
        color: #0764e9;
        background-color: #edf4fc;
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }
}

.user-preferences-dialog__desc {
    margin: 0 0 12px;
    font-size: 12px;
    line-height: 18px;
    color: #6b7280;
}

.user-preferences-dialog__textarea {
    width: 100%;
    min-height: 340px;
    padding: 12px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
    font-size: 13px;
    line-height: 20px;
    color: #1d2129;
    resize: vertical;
    border: 1px solid #dfe1e6;
    border-radius: 6px;
    outline: none;

    &:focus {
        border-color: #0764e9;
        box-shadow: 0 0 0 2px rgba(7, 100, 233, 0.12);
    }
}
</style>
